import { useIntl } from 'react-intl';
import { useRecoilState } from 'recoil';
import VerticalSlider from '~/components/Sliders/VerticalSlider';
import usePetImage from '~/hooks/image/usePetImage';
import useViewportEnvironment from '~/hooks/useViewportEnvironment';
import { imageSecondaryAmountState } from '~/state/atoms/image/imageAtoms';
import { OverlayLabel, OverlayRow } from '../../OverlayLabel';
import PetFusionColorSelector from './PetFusionColorSelector';

/**
 * Translate the stored primary-image alpha (0..100 %) into the
 * fraction of PET visible on screen. When PET is the primary the
 * primary alpha *is* the PET opacity; when PET is the secondary the
 * PET opacity is the complement.
 */
const computePetOpacity = (
    primaryAlphaPct: number,
    isPetPrimary: boolean
): number => (isPetPrimary ? primaryAlphaPct : 100 - primaryAlphaPct);

export const PetBlendingLabel = () => {
    const [imageSecondaryAmount] = useRecoilState(imageSecondaryAmountState);
    const { isPetPrimary } = usePetImage();
    const petOpacity = computePetOpacity(imageSecondaryAmount, isPetPrimary);

    return (
        <OverlayRow sx={{ alignItems: 'center', px: 0.25, ml: -0.25 }}>
            <OverlayLabel>PET</OverlayLabel>
            <OverlayLabel color="primary.main">{petOpacity}%</OverlayLabel>
            <PetFusionColorSelector />
        </OverlayRow>
    );
};

export const PetBlendSlider = () => {
    const intl = useIntl();
    const { updatePrimaryImageAlpha } = useViewportEnvironment();
    const { isPetPrimary } = usePetImage();
    const [imageSecondaryAmount, setImageSecondaryAmount] = useRecoilState(
        imageSecondaryAmountState
    );

    // Slider position tracks the *primary* image's alpha %, i.e. how
    // filled the rail is matches how visible the primary image is.
    // When PET is the primary this means dragging up increases PET
    // opacity; when PET is the secondary, dragging down increases PET
    // opacity (since the primary's CT/MR shrinks toward zero).
    const handleOpacityChange = (_: Event, newValue: number | number[]) => {
        const value = newValue as number;
        setImageSecondaryAmount(value);
        updatePrimaryImageAlpha(value);
    };

    return (
        <VerticalSlider
            key="petOpacity"
            value={imageSecondaryAmount}
            onChange={handleOpacityChange}
            min={0}
            max={100}
            valueLabelFormatter={(v) =>
                `${computePetOpacity(v, isPetPrimary)}%`
            }
            aria-label={intl.formatMessage({ id: 'petOpacity' })}
        />
    );
};
